import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { formatMAD, resolveMediaUrl } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag, Truck } from "lucide-react";
import { Link } from "react-router-dom";

export default function CartDrawer() {
    const { isOpen, setIsOpen, items, removeItem, updateQty, subtotal } = useCart();
    const { t, isRtl } = useI18n();
    const shipping = items.length > 0 ? 30 : 0;
    const total = subtotal + shipping;

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent
                side={isRtl ? "left" : "right"}
                className={`w-full sm:max-w-md bg-[#050505] ${
                    isRtl ? "border-r" : "border-l"
                } border-white/10 text-white p-0 flex flex-col`}
                data-testid="cart-drawer"
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/10 text-start">
                    <SheetTitle className="font-display uppercase tracking-tighter text-2xl font-black text-white">
                        {t("cart.title")} ({items.length})
                    </SheetTitle>
                    <SheetDescription className="sr-only">{t("cart.title")}</SheetDescription>
                </SheetHeader>

                {items.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-6">
                        <ShoppingBag size={40} strokeWidth={1} className="text-white/30" />
                        <div>
                            <p className="font-display text-xl uppercase font-black tracking-tighter">
                                {t("cart.empty")}
                            </p>
                            <p className="text-sm text-white/50 mt-2">{t("cart.empty_desc")}</p>
                        </div>
                        <Link
                            to="/collection"
                            onClick={() => setIsOpen(false)}
                            className="mt-2 inline-flex items-center justify-center px-8 h-12 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/90 transition-colors"
                            data-testid="cart-empty-shop-btn"
                        >
                            {t("cart.discover")}
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
                            {items.map((item) => (
                                <div
                                    key={item.key}
                                    className="flex gap-4 pb-4 border-b border-white/10"
                                    data-testid={`cart-item-${item.product_id}`}
                                >
                                    <div className="w-20 h-24 bg-white/5 flex-shrink-0 overflow-hidden">
                                        {item.image && (
                                            <img
                                                src={resolveMediaUrl(item.image)}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </div>
                                    <div className="flex-1 flex flex-col min-w-0">
                                        <p className="font-display text-sm font-bold uppercase tracking-tight truncate">
                                            {item.name}
                                        </p>
                                        {item.size && (
                                            <p className="text-xs uppercase tracking-[0.2em] text-white/50 mt-1">
                                                {t("checkout.size")}: {item.size}
                                            </p>
                                        )}
                                        <p className="text-sm mt-1">{formatMAD(item.price)}</p>
                                        <div className="mt-auto flex items-center justify-between">
                                            <div className="flex items-center border border-white/20">
                                                <button
                                                    onClick={() => updateQty(item.key, -1)}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-white/10 transition-colors"
                                                    data-testid={`cart-decrease-${item.product_id}`}
                                                    aria-label={t("cart.decrease")}
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <span className="w-8 text-center text-sm font-semibold">
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    onClick={() => updateQty(item.key, 1)}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-white/10 transition-colors"
                                                    data-testid={`cart-increase-${item.product_id}`}
                                                    aria-label={t("cart.increase")}
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => removeItem(item.key)}
                                                className="text-white/50 hover:text-white transition-colors"
                                                data-testid={`cart-remove-${item.product_id}`}
                                                aria-label={t("cart.remove")}
                                            >
                                                <Trash2 size={16} strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-white/10 px-6 py-5 space-y-4">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70">
                                <Truck size={14} strokeWidth={1.5} />
                                {t("cart.cod_available")}
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between text-white/70">
                                    <span>{t("cart.subtotal")}</span>
                                    <span data-testid="cart-subtotal">{formatMAD(subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-white/70">
                                    <span>{t("cart.shipping")}</span>
                                    <span>{formatMAD(shipping)}</span>
                                </div>
                                <div className="flex justify-between font-display uppercase font-bold text-base pt-2 border-t border-white/10">
                                    <span>{t("cart.total")}</span>
                                    <span data-testid="cart-total">{formatMAD(total)}</span>
                                </div>
                            </div>
                            <Link
                                to="/checkout"
                                onClick={() => setIsOpen(false)}
                                className="block w-full text-center py-4 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/90 transition-colors"
                                data-testid="cart-checkout-btn"
                            >
                                {t("cart.checkout")}
                            </Link>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
